import type {Ref} from 'react';
import React, {cloneElement, forwardRef} from 'react';
import {hasHoverSupport} from '@libs/DeviceCapabilities';
import mergeRefs from '@libs/mergeRefs';
import {getReturnValue} from '@libs/ValueUtils';
import ActiveHoverable from './ActiveHoverable';
import type HoverableProps from './types';

/**
 * It is necessary to create a Hoverable component instead of relying solely on Pressable support for hover state,
 * because nesting Pressables causes issues where the hovered state of the child cannot be easily propagated to the
 * parent. https://github.com/necolas/react-native-web/issues/1875
 */
function Hoverable(
  {isDisabled, ...props}: HoverableProps,
  ref: Ref<HTMLElement>,
) {
  // If Hoverable is disabled, just render the child without additional logic or event listeners.
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  if (isDisabled || !hasHoverSupport()) {
    const child = getReturnValue(props.children, false);
    // eslint-disable-next-line react-compiler/react-compiler, @typescript-eslint/no-explicit-any
    return cloneElement(child as React.ReactElement<any>, {
      ref: mergeRefs(ref, child.ref),
    });
  }

  return (
    <ActiveHoverable
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
      ref={ref}
    />
  );
}

export default forwardRef(Hoverable);
